import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RARITIES, COUNTRIES } from "@/lib/constants";
import { ComboInput } from "@/components/ComboInput";
import { useCatalogOptions } from "@/lib/useCatalogOptions";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const RARITY_LIST = ["Common", "Scarce", "Rare", "Very Rare", "Ultra Rare / Museum"];

function Field({ label, k, type = "text", value, onChange, ...rest }) {
  // For number fields, clear a leading 0 on focus and restore it on blur if left empty.
  const numberProps = type === "number" ? {
    onFocus: (e) => { if (String(value) === "0") onChange({ target: { value: "" } }); },
    onBlur: (e) => { if (e.target.value === "") onChange({ target: { value: "0" } }); },
  } : {};
  return (
    <div>
      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">{label}</label>
      <input data-testid={`ins-${k}-input`} type={type} value={value} onChange={onChange} {...numberProps} className="w-full surface border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400" {...rest} />
    </div>
  );
}

export default function AddInsulator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const opts = useCatalogOptions();
  const isAdmin = user && (user.role === "admin" || user.role === "owner");
  const [form, setForm] = useState({
    cd_number: "", name: "", maker: "", material: "Glass", color: "", rarity: "Common",
    rarity_index: 3, year: "", thread_type: "Threaded", base_type: "", patent_date: "",
    country: "", embossing: "", description: "", est_value_low: 0, est_value_high: 0,
  });
  const [images, setImages] = useState([]);
  const [captions, setCaptions] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        setImages((prev) => [...prev, url]);
      }
      toast.success("Photo(s) uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    const url = images[idx];
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setCaptions((c) => { const n = { ...c }; delete n[url]; return n; });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please enter at least a name for this insulator."); return; }
    setSaving(true);
    try {
      const payload = { ...form, rarity_index: parseInt(form.rarity_index) || 3, est_value_low: parseFloat(form.est_value_low) || 0, est_value_high: parseFloat(form.est_value_high) || 0, image_url: images[0] || "", images, image_captions: captions };
      const { data } = await api.post("/insulators", payload);
      if (isAdmin) {
        toast.success("Insulator added to the catalog");
        navigate(`/catalog/${data.id}`);
      } else {
        toast.success("Submitted! An admin will review it before it appears in the catalog.");
        navigate("/catalog");
      }
    } catch (err) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
      <p className="font-tech text-xs uppercase tracking-widest brand-text">Contribute</p>
      <h1 className="font-display text-4xl font-bold mb-6">Add an Insulator</h1>

      <form onSubmit={submit} className="surface border rounded-2xl p-6 space-y-5" data-testid="add-insulator-form">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="flex gap-3 flex-wrap">
            {images.map((img, i) => (
              <div key={img} className="w-32">
                <div className="relative w-32 h-32 rounded-xl overflow-hidden border" style={{ borderColor: "var(--page-border)" }}>
                  <img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-rose-600 text-white grid place-items-center text-xs">×</button>
                  {i === 0 && <span className="absolute bottom-0 inset-x-0 text-[9px] text-center text-white bg-black/50">Cover</span>}
                </div>
                <textarea
                  data-testid={`ins-photo-caption-${i}`}
                  value={captions[img] || ""}
                  onChange={(e) => setCaptions((c) => ({ ...c, [img]: e.target.value }))}
                  rows={2}
                  placeholder="Comment — damage, unique feature…"
                  className="w-32 mt-1 surface border rounded-lg px-2 py-1.5 text-xs"
                />
              </div>
            ))}
            {images.length === 0 && (
              <div className="w-32 h-32 rounded-xl border-2 border-dashed grid place-items-center" style={{ borderColor: "var(--page-border)" }}><ImagePlus className="w-8 h-8 text-muted-page" /></div>
            )}
          </div>
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold" data-testid="upload-photo-label">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Upload photo(s)"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleFile} data-testid="upload-photo-input" />
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <ComboInput label="Name / Style *" testId="ins-name-input" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} options={opts.names} placeholder="e.g. Beehive, Signal…" />
          <Field label="CD / U Number" k="cd_number" value={form.cd_number} onChange={upd("cd_number")} />
          <ComboInput label="Maker" testId="ins-maker-input" value={form.maker} onChange={(v) => setForm((f) => ({ ...f, maker: v }))} options={opts.makers} placeholder="Select or add a maker…" />
          <ComboInput label="Color" testId="ins-color-input" value={form.color} onChange={(v) => setForm((f) => ({ ...f, color: v }))} options={opts.colors} placeholder="e.g. Aqua, Emerald…" />
          <ComboInput label="Material" testId="ins-material-input" value={form.material} onChange={(v) => setForm((f) => ({ ...f, material: v }))} options={opts.materials} placeholder="e.g. Glass, Rubber…" />
          <div>
            <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Rarity</label>
            <select data-testid="ins-rarity-input" value={form.rarity} onChange={upd("rarity")} className="w-full surface border rounded-lg px-3 py-2.5">{RARITY_LIST.map((r) => <option key={r}>{r}</option>)}</select>
          </div>
          <Field label="Rarity Index (1–10)" k="rarity_index" type="number" min={1} max={10} value={form.rarity_index} onChange={upd("rarity_index")} />
          <Field label="Era / Year" k="year" value={form.year} onChange={upd("year")} />
          <ComboInput label="Thread Type" testId="ins-thread_type-input" value={form.thread_type} onChange={(v) => setForm((f) => ({ ...f, thread_type: v }))} options={opts.thread_types} />
          <ComboInput label="Base Type" testId="ins-base_type-input" value={form.base_type} onChange={(v) => setForm((f) => ({ ...f, base_type: v }))} options={opts.base_types} />
          <ComboInput label="Country of origin" testId="ins-country-input" value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v }))} options={opts.countries.length ? opts.countries : COUNTRIES} />
          <Field label="Est. Value Low ($)" k="est_value_low" type="number" min={0} value={form.est_value_low} onChange={upd("est_value_low")} />
          <Field label="Est. Value High ($)" k="est_value_high" type="number" min={0} value={form.est_value_high} onChange={upd("est_value_high")} />
        </div>

        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Embossing</label>
          <input data-testid="ins-embossing-input" value={form.embossing} onChange={upd("embossing")} placeholder="Exact raised lettering, e.g. HEMINGRAY-42 / MADE IN U.S.A. / PATENT MAY 2 1893" className="w-full surface border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>

        <div>
          <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Description</label>
          <textarea data-testid="ins-description-input" value={form.description} onChange={upd("description")} rows={4} className="w-full surface border rounded-lg px-3 py-2.5" />
        </div>

        <button data-testid="save-insulator-btn" disabled={saving} className="px-6 py-3 rounded-full font-semibold disabled:opacity-60" style={{ background: "var(--brand)", color: "#fff" }}>
          {saving ? "Saving…" : "Add to catalog"}
        </button>
      </form>
    </div>
  );
}
